<template>
  <div
    class="document-canvas"
    @dragover.prevent
    @drop="onDrop"
  >
    <!-- Header Image Area (always visible) -->
    <div
      class="doc-header-area"
      :class="{ 'header-banner-mode': headerMode === 'banner' }"
      @click="$emit('header-click')"
    >
      <div v-if="headerImage" class="doc-header-image" :class="{ 'banner-image': headerMode === 'banner' }">
        <img :src="headerImage" alt="Header" />
        <div class="doc-header-overlay">
          <i class="bx bx-pencil"></i> Alterar imagem
        </div>
      </div>
      <div v-else class="doc-header-placeholder">
        <i class="bx bx-image-add"></i>
        <span>{{ headerMode === 'banner' ? 'Clique para adicionar banner (fora a fora)' : 'Clique para adicionar logo / header' }}</span>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="blocks.length === 0" class="empty-canvas">
      <div class="empty-icon">
        <i class="bx bx-file-blank"></i>
      </div>
      <h5>Comece a construir seu documento</h5>
      <p>Arraste blocos da barra lateral ou clique neles para adicionar</p>
      <p class="empty-hint">Ou escolha um <strong>modelo pronto</strong> na aba "Modelos"</p>
    </div>

    <!-- Blocks -->
    <draggable
      v-else
      v-model="localBlocks"
      handle=".drag-handle"
      animation="200"
      ghost-class="ghost-block"
      @end="onReorder"
    >
      <div
        v-for="(block, index) in localBlocks"
        :key="block._id"
        class="block-card"
        :class="{
          'selected': selectedBlockId === block._id,
          'block-type-text': block.type === 'text',
          'block-type-heading': block.type === 'heading',
          'block-type-flight': block.type === 'flight-table',
          'block-type-accommodation': block.type === 'accommodation',
          'block-type-divider': block.type === 'divider'
        }"
        @click="$emit('select', block._id)"
      >
        <!-- Block Header -->
        <div class="block-header">
          <div class="drag-handle">
            <i class="bx bx-grid-vertical"></i>
          </div>
          <div class="block-label-row">
            <i :class="getBlockIcon(block.type)" class="block-icon-small"></i>
            <span class="block-type-label">{{ getBlockLabel(block.type) }}</span>
            <span class="block-index">#{{ index + 1 }}</span>
          </div>
          <div class="block-actions">
            <button
              type="button"
              class="btn-action"
              @click.stop="$emit('duplicate', block)"
              title="Duplicar"
            >
              <i class="bx bx-copy"></i>
            </button>
            <button
              type="button"
              class="btn-action btn-delete"
              @click.stop="$emit('remove', block._id)"
              title="Remover"
            >
              <i class="bx bx-trash"></i>
            </button>
          </div>
        </div>

        <!-- Block Preview -->
        <div class="block-preview">
          <!-- Text block -->
          <template v-if="block.type === 'text'">
            <div
              v-if="block.content"
              class="text-preview"
              v-html="block.content"
            ></div>
            <div v-else class="text-preview text-muted">
              <em>Clique para editar o conteúdo...</em>
            </div>
          </template>

          <!-- Heading block -->
          <template v-else-if="block.type === 'heading'">
            <div
              class="heading-preview"
              :class="'heading-' + ((block.settings && block.settings.style) || 'banner')"
              :style="getHeadingStyle(block)"
            >
              {{ block.content || 'TÍTULO DA SEÇÃO' }}
            </div>
          </template>

          <!-- Flight table block -->
          <template v-else-if="block.type === 'flight-table'">
            <div class="flight-preview">
              <div
                v-if="!block.settings || block.settings.showTitle !== false"
                class="flight-title-bar"
              >
                {{ (block.settings && block.settings.title) || 'VOOS CONFIRMADOS' }}
              </div>
              <table class="flight-table">
                <thead>
                  <tr>
                    <th>DATA</th>
                    <th>VOO</th>
                    <th>DE / PARA</th>
                    <th>SAÍDA / CHEGADA</th>
                    <th v-if="isFlightAuto(block) && flightShowLocalizador(block)">LOCALIZ.</th>
                    <th v-if="isFlightAuto(block) && flightShowEticket(block)">E-TICKET</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>23/08</td>
                    <td>LATAM 3711</td>
                    <td>São Paulo / Miami</td>
                    <td>21:30 / 06:25</td>
                    <td v-if="isFlightAuto(block) && flightShowLocalizador(block)"><code>X7Y2AB</code></td>
                    <td v-if="isFlightAuto(block) && flightShowEticket(block)"><code>957-2401234567</code></td>
                  </tr>
                  <tr>
                    <td>24/08</td>
                    <td>United 2401</td>
                    <td>Miami / Chicago</td>
                    <td>08:20 / 09:36</td>
                    <td v-if="isFlightAuto(block) && flightShowLocalizador(block)"><code>K8N9TR</code></td>
                    <td v-if="isFlightAuto(block) && flightShowEticket(block)"><code>016-7654321098</code></td>
                  </tr>
                  <tr>
                    <td>30/08</td>
                    <td>United 845</td>
                    <td>Chicago / São Paulo</td>
                    <td>21:20 / 09:30</td>
                    <td v-if="isFlightAuto(block) && flightShowLocalizador(block)"><code>M3P7QX</code></td>
                    <td v-if="isFlightAuto(block) && flightShowEticket(block)"><code>016-7654321099</code></td>
                  </tr>
                </tbody>
              </table>
              <div v-if="!isFlightAuto(block)" class="flight-meta">
                <span v-if="flightShowLocalizador(block)">
                  <strong>Localizador:</strong>
                  <code>{{ (block.settings && block.settings.manual_locator) || '{$localizador}' }}</code>
                </span>
                <span v-if="flightShowEticket(block)">
                  <strong>E-Ticket:</strong>
                  <code>{{ (block.settings && block.settings.manual_eticket) || '{$eticket}' }}</code>
                </span>
              </div>
            </div>
          </template>

          <!-- Accommodation block -->
          <template v-else-if="block.type === 'accommodation'">
            <div class="accommodation-preview">
              <div
                v-if="!block.settings || block.settings.showTitle !== false"
                class="flight-title-bar"
              >
                {{ (block.settings && block.settings.title) || 'HOSPEDAGEM' }}
              </div>
              <table class="accommodation-table">
                <tbody>
                  <tr>
                    <th>Hotel</th>
                    <td>{{ getAccommodationValue(block, 'hotel_name', 'Hotel Exemplo') }}</td>
                  </tr>
                  <tr>
                    <th>Endereço</th>
                    <td>{{ getAccommodationValue(block, 'hotel_address', 'Av. Exemplo, 100 - Cidade/UF') }}</td>
                  </tr>
                  <tr>
                    <th>Check-in / Check-out</th>
                    <td>{{ getAccommodationDates(block) }}</td>
                  </tr>
                  <tr>
                    <th>Tipo de quarto</th>
                    <td>{{ getAccommodationValue(block, 'room_type', 'Duplo Cama Casal') }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="accommodation-source-tag">
                <i class="bx bx-info-circle"></i>
                <span v-if="(block.settings && block.settings.source) === 'manual'">
                  Fonte: Manual (mesmo hotel para todos)
                </span>
                <span v-else-if="(block.settings && block.settings.source) === 'misto'">
                  Fonte: Misto (módulo + fallback manual)
                </span>
                <span v-else>
                  Fonte: Automático (módulo de Hospedagem)
                </span>
              </div>
            </div>
          </template>

          <!-- Divider block -->
          <template v-else-if="block.type === 'divider'">
            <hr
              class="divider-preview"
              :class="'divider-' + ((block.settings && block.settings.style) || 'solid')"
            />
          </template>
        </div>
      </div>
    </draggable>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'DocumentCanvas',
  components: { draggable },
  props: {
    blocks: { type: Array, default: function () { return [] } },
    selectedBlockId: { type: String, default: null },
    headerImage: { type: String, default: null },
    headerMode: { type: String, default: 'logo' }
  },
  data: function () {
    return {
      localBlocks: []
    }
  },
  watch: {
    blocks: {
      handler: function (val) {
        this.localBlocks = val ? val.slice() : []
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    getBlockIcon: function (type) {
      var icons = {
        text: 'bx bx-text',
        heading: 'bx bx-heading',
        'flight-table': 'bx bx-table',
        accommodation: 'bx bx-bed',
        divider: 'bx bx-minus'
      }
      return icons[type] || 'bx bx-cube'
    },
    getBlockLabel: function (type) {
      var labels = {
        text: 'Texto Rico',
        heading: 'Título de Seção',
        'flight-table': 'Tabela de Voos',
        accommodation: 'Hospedagem',
        divider: 'Separador'
      }
      return labels[type] || 'Bloco'
    },
    isFlightAuto: function (block) {
      var s = (block && block.settings) || {}
      return (s.source || 'auto') === 'auto' || s.source === 'misto'
    },
    flightShowLocalizador: function (block) {
      var s = (block && block.settings) || {}
      return s.showLocalizador !== false
    },
    flightShowEticket: function (block) {
      var s = (block && block.settings) || {}
      return !!s.showEticket
    },
    getAccommodationValue: function (block, field, placeholder) {
      var s = block.settings || {}
      if (s.source === 'manual' || s.source === 'misto') {
        if (s[field]) return s[field]
      }
      return placeholder
    },
    getAccommodationDates: function (block) {
      var s = block.settings || {}
      var ci = ''
      var co = ''
      if (s.source === 'manual' || s.source === 'misto') {
        ci = s.check_in ? this.formatDate(s.check_in) : ''
        co = s.check_out ? this.formatDate(s.check_out) : ''
      }
      if (!ci && !co) return '15/05/2026 → 18/05/2026'
      return (ci || '___') + ' → ' + (co || '___')
    },
    formatDate: function (iso) {
      var d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      var day = String(d.getDate()).padStart(2, '0')
      var mon = String(d.getMonth() + 1).padStart(2, '0')
      return day + '/' + mon + '/' + d.getFullYear()
    },
    getHeadingStyle: function (block) {
      var s = block.settings || {}
      var style = {
        textAlign: s.alignment || 'center'
      }
      if (s.style === 'banner' || !s.style) {
        if (s.bgColor) style.background = s.bgColor
        if (s.textColor) style.color = s.textColor
      } else {
        // simple/underline: bgColor used for text color, textColor ignored
        if (s.bgColor) style.color = s.bgColor
        if (s.style === 'underline' && s.bgColor) style.borderBottomColor = s.bgColor
      }
      return style
    },
    onReorder: function () {
      this.$emit('reorder', this.localBlocks)
    },
    onDrop: function (event) {
      var data = event.dataTransfer.getData('block-type')
      if (data) {
        try {
          var blockType = JSON.parse(data)
          this.$emit('drop-new', blockType.type)
        } catch (e) {
          console.error('Invalid drop data', e)
        }
      }
    }
  }
}
</script>

<style scoped>
.document-canvas {
  min-height: 400px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 24px;
}

/* Header area */
.doc-header-area {
  margin-bottom: 16px;
  cursor: pointer;
}

.doc-header-image {
  text-align: center;
  padding: 16px 20px;
  border-bottom: 1px solid #cbd5e0;
  position: relative;
}

.doc-header-image img {
  max-width: 400px;
  max-height: 90px;
}

/* Banner mode */
.header-banner-mode {
  margin: -24px -24px 0 -24px;
  border-radius: 0;
}

.banner-image {
  padding: 0 !important;
  border-bottom: none !important;
}

.banner-image img {
  max-width: 100% !important;
  max-height: 120px !important;
  width: 100%;
  display: block;
  object-fit: cover;
}

.doc-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(85, 110, 230, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #556ee6;
  opacity: 0;
  transition: opacity 0.15s;
  border-radius: 4px;
}

.doc-header-image:hover .doc-header-overlay {
  opacity: 1;
}

.doc-header-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  border: 2px dashed #d0d5dd;
  border-radius: 6px;
  color: #adb5bd;
  font-size: 13px;
  transition: all 0.15s;
}

.doc-header-placeholder i {
  font-size: 20px;
}

.doc-header-placeholder:hover {
  border-color: #556ee6;
  color: #556ee6;
  background: #f8f9ff;
}

/* Empty state */
.empty-canvas {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 50%;
  margin-bottom: 16px;
}

.empty-icon i {
  font-size: 28px;
  color: #556ee6;
}

.empty-canvas h5 {
  font-size: 15px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.empty-canvas p {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

.empty-hint {
  margin-top: 8px !important;
  font-size: 12px !important;
}

/* Block cards (matching card-builder field-card pattern) */
.block-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.block-card:hover {
  border-color: #d0d5dd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.block-card.selected {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.15);
}

.ghost-block {
  opacity: 0.5;
  background: #f3f4f6;
}

/* Block header */
.block-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #f3f4f6;
}

.drag-handle {
  cursor: grab;
  color: #adb5bd;
  padding: 4px;
  display: flex;
}

.drag-handle:hover { color: #556ee6; }
.drag-handle:active { cursor: grabbing; }

.block-label-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.block-icon-small {
  font-size: 16px;
  color: #556ee6;
}

.block-type-heading .block-icon-small { color: #e67e22; }
.block-type-flight .block-icon-small { color: #7b1fa2; }
.block-type-divider .block-icon-small { color: #616161; }

.block-type-label {
  font-size: 12px;
  font-weight: 500;
  color: #495057;
}

.block-index {
  font-size: 10px;
  color: #adb5bd;
  background: #f8f9fa;
  padding: 1px 6px;
  border-radius: 8px;
}

.block-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s;
}

.block-card:hover .block-actions { opacity: 1; }

.btn-action {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: #6c757d;
  cursor: pointer;
}

.btn-action:hover { background: #f3f4f6; }
.btn-action.btn-delete:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* Block previews */
.block-preview {
  padding: 12px 16px;
}

/* Text preview */
.text-preview {
  font-family: 'DejaVu Sans', Arial, Helvetica, sans-serif;
  font-size: 12px;
  line-height: 1.6;
  color: #344054;
  max-height: 200px;
  overflow: hidden;
  position: relative;
}

.text-preview::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(transparent, #fff);
  pointer-events: none;
}

.text-preview.text-muted {
  color: #adb5bd;
}

.text-preview.text-muted::after { display: none; }

/* Heading preview */
.heading-preview {
  font-size: 14px;
  font-weight: bold;
  color: #344054;
}

.heading-preview.heading-banner {
  background: #2c3e50;
  color: #fff;
  padding: 10px 16px;
  border-radius: 2px;
}

.heading-preview.heading-simple {
  font-style: italic;
}

.heading-preview.heading-underline {
  border-bottom: 2px solid #2c3e50;
  padding-bottom: 4px;
}

/* Flight table preview */
.flight-preview { font-size: 12px; }

.flight-title-bar {
  background: #2c3e50;
  color: #fff;
  padding: 8px 12px;
  text-align: center;
  font-weight: bold;
  font-size: 12px;
  border-radius: 2px 2px 0 0;
}

.flight-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.flight-table th {
  background: #f8f9fa;
  padding: 6px 10px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border: 1px solid #e9ecef;
  font-size: 10px;
}

.flight-table td {
  padding: 5px 10px;
  border: 1px solid #e9ecef;
  color: #495057;
  font-size: 11px;
}

.flight-table tbody tr:nth-child(even) {
  background: #fafafa;
}

.flight-meta {
  padding: 8px 0 0;
  display: flex;
  gap: 16px;
  font-size: 11px;
  color: #495057;
}

.flight-meta code {
  background: #f0f4ff;
  color: #556ee6;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 10px;
}

/* Divider preview */
.divider-preview {
  margin: 8px 0;
  border: 0;
}

.divider-solid { border-top: 1px solid #dee2e6; }
.divider-dashed { border-top: 1px dashed #dee2e6; }
.divider-double { border-top: 3px double #dee2e6; }

/* Accommodation preview */
.accommodation-preview { font-size: 12px; }

.accommodation-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 6px;
}

.accommodation-table th {
  background: #f1f5f9;
  padding: 8px 10px;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  width: 34%;
  border-bottom: 1px solid #e2e8f0;
}

.accommodation-table td {
  padding: 8px 10px;
  font-size: 12px;
  color: #334155;
  border-bottom: 1px solid #e2e8f0;
}

.accommodation-source-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  margin-top: 6px;
  background: #f0fdf4;
  border: 1px dashed #86efac;
  border-radius: 4px;
  font-size: 11px;
  color: #166534;
}

.accommodation-source-tag i { font-size: 14px; }
</style>
